import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { EmpresasDataModel } from '../../../../core/models/empresa.data.model';
import { UsuarioFormValue } from '../../../../core/models/usuario.data.model';
import { RolesDto } from '../../../../core/models/roles.model';
import { SelectOption, SelectComponent } from '../../../../shared/select/select';
import { FORM_LIMITS } from '../../../../core/utils/validation.utils';

interface FormModel {
  nombre: string;
  usuario: string;
  password: string;
  empresaId: string;
  rolIds: string[];
}

@Component({
  selector: 'app-usuarios-form',
  standalone: true,
  imports: [FormsModule, SelectComponent],
  templateUrl: './usuarios-form.html',
  styleUrl: './usuarios-form.css',
})
export class UsuariosFormComponent implements OnChanges {
  @Input() usuario: UsuarioFormValue | null = null;
  @Input() empresas: EmpresasDataModel[] = [];
  @Input() roles: RolesDto[] = [];
  @Input() serverError = '';
  @Output() readonly saved = new EventEmitter<UsuarioFormValue>();
  @Output() readonly cancelled = new EventEmitter<void>();

  protected readonly form: FormModel = this.emptyForm();
  protected error = '';

  protected get empresaOptions(): SelectOption[] {
    return this.empresas.map((empresa) => ({ label: empresa.empresa, value: empresa.empresaId }));
  }

  ngOnChanges(): void {
    Object.assign(this.form, {
      nombre: this.usuario?.nombre ?? '',
      usuario: this.usuario?.usuario ?? '',
      password: '',
      empresaId: this.usuario?.empresaId ?? '',
      rolIds: this.usuario?.rolIds ?? [],
    });
    this.error = '';
  }

  protected submit(): void {
    if (!this.form.nombre.trim() || !this.form.usuario.trim()) {
      this.error = 'El nombre y el usuario son obligatorios.';
      return;
    }
    if (
      this.form.nombre.trim().length > FORM_LIMITS.nameMaxLength ||
      this.form.usuario.trim().length < FORM_LIMITS.usernameMinLength ||
      this.form.usuario.trim().length > FORM_LIMITS.usernameMaxLength ||
      !/^[a-zA-Z0-9._-]+$/.test(this.form.usuario.trim())
    ) {
      this.error = 'Revisa la longitud y el formato del nombre de usuario.';
      return;
    }

    if (!this.usuario && !this.form.password) {
      this.error = 'La contraseña es obligatoria para crear un usuario.';
      return;
    }
    if (
      this.form.password &&
      (this.form.password.length < FORM_LIMITS.passwordMinLength ||
        this.form.password.length > FORM_LIMITS.passwordMaxLength)
    ) {
      this.error = `La contraseña debe tener entre ${FORM_LIMITS.passwordMinLength} y ${FORM_LIMITS.passwordMaxLength} caracteres.`;
      return;
    }

    if (this.form.password && !/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(this.form.password)) {
      this.error = 'La contraseña debe incluir mayúsculas, minúsculas y números.';
      return;
    }

    this.error = '';
    this.saved.emit({
      idUsuario: this.usuario?.idUsuario,
      nombre: this.form.nombre.trim(),
      usuario: this.form.usuario.trim(),
      password: this.form.password || undefined,
      empresaId: this.form.empresaId || null,
      rolIds: this.form.rolIds,
    });
  }

  private emptyForm(): FormModel {
    return { nombre: '', usuario: '', password: '', empresaId: '', rolIds: [] };
  }

  onRoleChange(rolId: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;

    if (checked) {
      this.form.rolIds = [...this.form.rolIds, rolId];
    } else {
      this.form.rolIds = this.form.rolIds.filter((id) => id !== rolId);
    }
  }
}
